import React, { useState, useRef, useEffect } from "react";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Logo } from "../Logo/Logo";
import { Spinner } from "@heroui/react";
import { useDeliveryAction, useOrder, usePickupAction } from "../../api";
import { useParams } from "react-router-dom";

interface ModernPinEntryProps {}

export const ModernPinEntry: React.FC<ModernPinEntryProps> = () => {
  const { orderId } = useParams();
  const [pin, setPin] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPollingRef = useRef(false);

  // Use React Query mutations
  const pickupMutation = usePickupAction();
  const deliveryMutation = useDeliveryAction();

  const { data: order, refetch } = useOrder(orderId);

  // Check for order state changes in the background
  const checkForStateChange = () => {
    if (!order || isPollingRef.current) return;

    isPollingRef.current = true;
    const initialState = order.state;
    let attempts = 0;
    const maxAttempts = 15; // 15 seconds max
    const pollInterval = 1000; // 1 second intervals

    const pollForStateChange = async (): Promise<void> => {
      if (attempts >= maxAttempts) {
        isPollingRef.current = false;
        return;
      }

      try {
        const result = await refetch();
        const updatedOrder = result.data;

        if (updatedOrder && updatedOrder.state !== initialState) {
          isPollingRef.current = false;
          return;
        }

        attempts++;
        timeoutRef.current = setTimeout(
          () => pollForStateChange(),
          pollInterval
        );
      } catch (error) {
        console.error("Error polling for state change:", error);
        isPollingRef.current = false;
      }
    };

    // Start polling in the background
    pollForStateChange();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const registerPickUp = async (pin: string) => {
    if (!orderId) return false;

    try {
      const result = await pickupMutation.mutateAsync(
        { orderId, pin },
        {
          onSuccess: () => {},
          onError: (error) => {
            console.error("Error in registerPickUp:", error);
            throw error;
          },
        }
      );
      if (result === true) {
        setSuccessMessage("Pickup successful!");
        checkForStateChange();
      } else {
        setErrorMessage("Pickup failed! Please check the PIN and try again.");
      }
      return !!result;
    } catch (error) {
      console.error("Error in registerPickUp:", error);
      throw error;
    }
  };

  const registerDelivery = async (pin: string) => {
    if (!orderId) return false;

    try {
      const result = await deliveryMutation.mutateAsync(
        { orderId, pin },
        {
          onSuccess: () => {},
          onError: (error) => {
            console.error("Error in registerDelivery:", error);
            throw error;
          },
        }
      );
      if (result === true) {
        setSuccessMessage("Delivery successful!");
        checkForStateChange();
      } else {
        setErrorMessage("Delivery failed! Please check the PIN and try again.");
      }
      return !!result;
    } catch (error) {
      console.error("Error in registerDelivery:", error);
      throw error;
    }
  };

  useEffect(() => {
    // Focus the input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4) return;

    try {
      const result = await (order?.state === "ACCEPTED" ||
      order?.state === "ASSIGNED"
        ? registerPickUp(pin)
        : registerDelivery(pin));
      if (result === true) {
        setPin("");
        setSuccessMessage(
          `${
            order?.state === "ACCEPTED" || order?.state === "ASSIGNED"
              ? "Pickup"
              : "Delivery"
          } successful!`
        );
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setPin(value);
    if (errorMessage) {
      setErrorMessage("");
    }
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const getInstructions = () => {
    switch (order?.state) {
      case "ACCEPTED":
      case "ASSIGNED":
        return "Enter the pickup PIN to confirm order pickup";
      case "IN TRANSIT":
        return "Enter the delivery PIN to confirm order delivery";
      default:
        return "Enter the PIN to proceed";
    }
  };

  const getButtonText = () => {
    switch (order?.state) {
      case "ACCEPTED":
      case "ASSIGNED":
        return "Confirm Pickup";
      case "IN TRANSIT":
        return "Confirm Delivery";
      default:
        return "Submit";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <Card className="bg-gray-700/60 backdrop-blur-sm border border-gray-600/30 shadow-2xl rounded-3xl">
          <CardHeader className="pb-0 pt-8 px-8 flex justify-center">
            <div className="text-center space-y-3 w-full">
              <h1 className="text-2xl font-bold text-white tracking-tight">
                Order #{order?.orderNumber || orderId}
              </h1>
              <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                {getInstructions()}
              </p>
            </div>
          </CardHeader>

          <CardBody className="px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Input
                  ref={inputRef}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pin}
                  onChange={handlePinChange}
                  placeholder="Enter 4-digit PIN"
                  maxLength={4}
                  autoFocus
                  isDisabled={
                    pickupMutation.isPending || deliveryMutation.isPending
                  }
                  endContent={
                    pickupMutation.isPending || deliveryMutation.isPending ? (
                      <Spinner size="sm" />
                    ) : null
                  }
                  classNames={{
                    base: "w-full",
                    mainWrapper: "w-full",
                    input: [
                      "text-center text-3xl tracking-widest font-mono font-bold",
                      "bg-gray-700/50 border-gray-600/50 text-white",
                      "placeholder:text-gray-500/70",
                      "focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20",
                      "transition-all duration-200",
                      "focus:outline-none",
                      "focus:ring-0",
                    ],
                    inputWrapper: [
                      "bg-gray-600/40 border-gray-500/40 rounded-2xl",
                      "hover:border-gray-400/60 hover:bg-gray-600/50",
                      "focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20",
                      "transition-all duration-200",
                      "focus-within:outline-none",
                    ],
                  }}
                />
              </div>

              <Button
                type="submit"
                disabled={
                  pin.length !== 4 ||
                  pickupMutation.isPending ||
                  deliveryMutation.isPending
                }
                className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 hover:from-blue-700 hover:via-blue-600 hover:to-purple-700 text-white font-semibold py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                size="lg"
              >
                {pickupMutation.isPending || deliveryMutation.isPending ? (
                  <div className="flex justify-center items-center space-x-3">
                    <Spinner size="sm" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  getButtonText()
                )}
              </Button>
            </form>

            {errorMessage && (
              <div className="mt-6 p-4 bg-red-900/20 border border-red-600/30 rounded-2xl text-center backdrop-blur-sm">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-red-300 text-sm font-medium">
                  {errorMessage}
                </p>
              </div>
            )}

            {successMessage && (
              <div className="mt-6 p-4 bg-green-900/20 rounded-2xl text-center backdrop-blur-sm">
                <div className="flex items-center justify-center mb-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-green-300 text-sm font-medium">
                  {successMessage}
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
